import { collection, addDoc } from 'firebase/firestore';
import type { AIActivityRecord } from '../types';
import { getDb } from './firebase';
import { getCachedAIAgent } from './aiDetector';
import { analyzeTextForAttacks, logSecurityEvent, isRateLimitedForSecurity } from './securityAnalyzer';

const AI_ACTIVITY_COLLECTION = 'ai_activity';

/**
 * Registra en la BD la actividad de una IA/bot (prompt, acción, visita).
 * Solo escribe si previamente se detectó que el visitante es IA/bot.
 */
export async function logAIActivity(
  siteId: string,
  payload: {
    promptOrActivity: string;
    eventType: AIActivityRecord['eventType'];
    metadata?: Record<string, unknown>;
  }
): Promise<void> {
  const agent = getCachedAIAgent();
  if (!agent) return;

  const db = getDb();
  if (!db) return;

  try {
    const record: Omit<AIActivityRecord, 'recordedAt'> & { recordedAt: number } = {
      siteId,
      agentType: agent.type,
      userAgent: agent.userAgent,
      promptOrActivity: payload.promptOrActivity,
      eventType: payload.eventType,
      url: typeof window !== 'undefined' ? window.location.href : '',
      recordedAt: Date.now(),
      metadata: payload.metadata,
    };
    await addDoc(collection(db, 'sites', siteId, AI_ACTIVITY_COLLECTION), record);

    // Analizar el prompt/actividad en busca de patrones de ataque o prompt injection
    if (payload.eventType !== 'page_view') {
      const analysis = analyzeTextForAttacks(payload.promptOrActivity);
      if (analysis.isSuspicious && !isRateLimitedForSecurity()) {
        await logSecurityEvent(siteId, {
          eventType: analysis.type ?? 'prompt_injection',
          severity: analysis.severity ?? 'medium',
          source: 'ai_activity',
          sample: payload.promptOrActivity,
          metadata: { reasons: analysis.reasons, originalEventType: payload.eventType },
        });
      }
    }
  } catch (e) {
    if (typeof console !== 'undefined') {
      console.warn('[Kenbun] Error logging AI activity:', e);
    }
  }
}

/**
 * Si el visitante es IA/bot, registra una visita a la página actual.
 * Llamar al cargar el widget (una vez por sesión).
 */
export async function logAIPageViewIfNeeded(siteId: string): Promise<void> {
  const agent = getCachedAIAgent();
  if (!agent) return;

  const db = getDb();
  if (!db) return;

  try {
    const record: Omit<AIActivityRecord, 'recordedAt'> & { recordedAt: number } = {
      siteId,
      agentType: agent.type,
      userAgent: agent.userAgent,
      promptOrActivity: 'page_view',
      eventType: 'page_view',
      url: typeof window !== 'undefined' ? window.location.href : '',
      recordedAt: Date.now(),
    };
    await addDoc(collection(db, 'sites', siteId, AI_ACTIVITY_COLLECTION), record);

    // Registrar también un evento de seguridad informativo de presencia de bot/IA en el sitio
    if (!isRateLimitedForSecurity()) {
      await logSecurityEvent(siteId, {
        eventType: 'ai_bot_activity',
        severity: 'low',
        source: 'page_view',
        sample: 'AI / bot page_view detectado por Kenbun',
        metadata: { agentType: agent.type },
      });
    }
  } catch (e) {
    if (typeof console !== 'undefined') {
      console.warn('[Kenbun] Error logging AI page view:', e);
    }
  }
}

/**
 * API para que el sitio o otro script registre un prompt/actividad custom cuando detecte IA.
 * Ej: window.Kenbun?.logAIPrompt?.('mi-sitio', 'búsqueda: zapatillas azules', { searchQuery: '...' });
 */
export function registerGlobalLogger(): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as { Kenbun?: { logAIPrompt?: (siteId: string, promptOrActivity: string, metadata?: Record<string, unknown>) => void } };
  if (!w.Kenbun) w.Kenbun = {};
  w.Kenbun.logAIPrompt = (siteId: string, promptOrActivity: string, metadata?: Record<string, unknown>) => {
    logAIActivity(siteId, {
      promptOrActivity,
      eventType: 'custom',
      metadata,
    });
  };
}
